import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Logo from '../shared/brand/Logo';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { useHistory } from 'react-router-dom';
import {
  PiPlusLight,
  PiTrashLight,
  PiTableLight,
  PiArrowLeftLight,
  PiCopyLight,
} from 'react-icons/pi';
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTableTemplate,
} from '../../store/actions/BatchTables/BatchTablesActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig, OrgWideToggle } from '../shared/FormComponents';
import { FormSchema } from '../../utils/validation/form-schema';
import { Collaborator } from '../shared/CollaboratorSelector';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import Badge from '../shared/TailwindComponents/Badge';
import GlassCard from '../shared/brand/GlassCard';
import { Well } from '../shared/brand/Field';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import { ColumnDataType } from '../../types/batchTables';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, tableColumnsFormTourSteps, tableColumnItemTourSteps } from '../shared/FeatureTour';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import TemplateTypeSwitcher from './TemplateTypeSwitcher';
import OrdinalBadge from '../shared/brand/OrdinalBadge';

// ─── Column Editor Custom Component ────────────────────────────────────

interface ColumnItem {
  name: string;
  query: string;
  data_type: ColumnDataType;
}

const dataTypeOptions: DropdownOption[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'currency', label: 'Currency' },
];

interface ColumnsEditorProps {
  id?: string;
  name?: string;
  label?: string;
  value: ColumnItem[];
  onChange: (value: ColumnItem[]) => void;
  disabled?: boolean;
  required?: boolean;
  isGlobal?: boolean;
}

const ColumnsEditor: React.FC<ColumnsEditorProps> = ({
  value = [],
  onChange,
  disabled,
  isGlobal,
}) => {
  const { t } = useTranslation();
  const columns = value || [];
  const isReadOnly = disabled || isGlobal;

  const handleColumnChange = (index: number, field: keyof ColumnItem, val: string) => {
    const updated = [...columns];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const addColumn = () => {
    onChange([...columns, { name: '', query: '', data_type: 'text' as ColumnDataType }]);
  };

  const removeColumn = (index: number) => {
    const updated = [...columns];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div>
      {columns.length === 0 && (
        <div className="text-center py-12 border border-dashed border-hairline rounded-lg">
          <PiTableLight className="w-10 h-10 mx-auto text-fg-muted mb-3" />
          <p className="text-sm text-fg-muted">
            {t('No columns defined. Add your first column to get started.')}
          </p>
          {!isReadOnly && (
            <Button
              type="button"
              variant="neutral"
              size="small"
              onClick={addColumn}
              className="mt-3"
            >
              <PiPlusLight className="w-4 h-4 me-1" />
              {t('Add Column')}
            </Button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {columns.map((col, index) => (
          <Well
            key={index}
            className="p-4 relative group"
            {...(index === 0 ? { 'data-tour-id': 'table-col-item' } : {})}
          >
            {/* Remove button - top right, hover reveal */}
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => removeColumn(index)}
                className="absolute top-3 end-3 p-1.5 rounded-md text-fg-muted hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition opacity-0 group-hover:opacity-100"
                title={t('Remove column')}
              >
                <PiTrashLight className="w-4 h-4" />
              </button>
            )}

            {/* Row 1: Column number + Name + Type */}
            <div className="flex items-start gap-3">
              <div className="flex items-center pt-2">
                <OrdinalBadge value={index + 1} />
              </div>
              <div className="flex-1 min-w-0">
                <Input
                  id={`col-name-${index}`}
                  type="text"
                  label={t('Column Name')}
                  placeholder={t('e.g., Effective Date')}
                  value={col.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleColumnChange(index, 'name', e.target.value)
                  }
                  disabled={isReadOnly}
                />
              </div>
              <div className="flex-shrink-0">
                <Dropdown
                  id={`col-type-${index}`}
                  label={t('Type')}
                  options={dataTypeOptions}
                  value={col.data_type}
                  onChange={(val) =>
                    handleColumnChange(index, 'data_type', Array.isArray(val) ? val[0] : val)
                  }
                />
              </div>
            </div>

            {/* Row 2: Query (full width, indented to align with name) */}
            <div className="mt-2 ps-9">
              <Input
                id={`col-query-${index}`}
                type="text"
                label={t('AI Query')}
                placeholder={t('e.g., What is the effective date of the agreement?')}
                value={col.query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleColumnChange(index, 'query', e.target.value)
                }
                disabled={isReadOnly}
              />
            </div>
          </Well>
        ))}
      </div>

      {/* Footer: add column + count */}
      {columns.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <Badge color="gray">{columns.length}</Badge>
            <span>{t('column' + (columns.length !== 1 ? 's' : '') + ' defined')}</span>
          </div>
          {!isReadOnly && (
            <Button
              type="button"
              variant="neutral"
              size="small"
              onClick={addColumn}
              data-tour-id="table-col-add-button"
            >
              <PiPlusLight className="w-4 h-4 me-1" />
              {t('Add Column')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Category Options ──────────────────────────────────────────────────

const getCategoryOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: '', label: t('None') },
  { value: 'due_diligence', label: t('Due Diligence') },
  { value: 'compliance', label: t('Compliance') },
  { value: 'real_estate', label: t('Real Estate') },
  { value: 'corporate', label: t('Corporate') },
  { value: 'litigation', label: t('Litigation') },
  { value: 'ip', label: t('Intellectual Property') },
  { value: 'employment', label: t('Employment') },
  { value: 'general', label: t('General') },
];

// ─── Main Form ─────────────────────────────────────────────────────────

interface TableColumnsFormProps {
  templateId?: string;
}

const TableColumnsForm: React.FC<TableColumnsFormProps> = ({ templateId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isEditing = !!templateId;

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingTemplate, setExistingTemplate] = useState<any>(null);
  const [isOrgWide, setIsOrgWide] = useState(false);

  const isGlobal = existingTemplate?.is_global;

  // ─── Tour ──────────────────────────────────────────────────────────
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'table-columns-form',
    steps: tableColumnsFormTourSteps,
    readyToShow: !isLoading && !isGlobal,
    autoStartDelay: 600,
  });

  const { isRunning: isColTourRunning, handleJoyrideCallback: handleColTourCallback } = useFeatureTour({
    tourId: 'table-column-items',
    steps: tableColumnItemTourSteps,
    readyToShow: !isLoading && !isGlobal,
    autoStartDelay: 600,
  });

  // ─── Load data ──────────────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const promises: Promise<any>[] = [dispatch(fetchOrgPeople())];
        if (templateId) {
          promises.push(dispatch(getTemplate(templateId) as any));
        }
        const results = await Promise.all(promises);
        if (templateId && results[1]) {
          setExistingTemplate(results[1]);
          setIsOrgWide(!!results[1].is_org_wide);
        }
      } catch (error: any) {
        console.error('[TableColumnsForm] Error loading data:', error?.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, templateId]);

  // ─── Initial Values ─────────────────────────────────────────────────

  const initialValues = useMemo(() => ({
    name: existingTemplate?.name || '',
    description: existingTemplate?.description || '',
    category: existingTemplate?.category || '',
    columns: existingTemplate?.columns?.length
      ? existingTemplate.columns.map((c: any) => ({
          name: c.name || '',
          query: c.query || '',
          data_type: c.data_type || 'text',
        }))
      : [{ name: '', query: '', data_type: 'text' as ColumnDataType }],
    is_org_wide: !!existingTemplate?.is_org_wide,
    collaborators: (existingTemplate?.shared_with || []).map((u: any) => ({
      user_id: u.user_id,
      collaborator_uuid: u.user_id,
      access: (u.access === 'admin' ? 'edit' : u.access) as 'edit' | 'view',
    })).filter((c: any) => c.access !== 'admin'),
  }), [existingTemplate]);

  // ─── Form Sections ──────────────────────────────────────────────────

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    const sections: BaseFormSectionConfig[] = [
      {
        dataTourId: 'table-cols-details',
        title: t('Template Details'),
        description: t('Name this template and say what it is for.'),
        // Split layout (feedback round 3: "too much scrolling"): the small
        // facts sit in a side column that stays in view; the columns get the
        // rest of the width as a workspace.
        aside: true,
        gridCols: 1,
        fields: [
          {
            name: 'name',
            type: 'input',
            label: t('Template Name'),
            placeholder: t('e.g., Due Diligence — Standard Review'),
            required: true,
            gridSize: 'half',
            disabled: isGlobal,
          } as BaseFormFieldConfig,
          {
            name: 'category',
            type: 'dropdown',
            label: t('Category'),
            options: getCategoryOptions(t),
            placeholder: t('Select a category'),
            gridSize: 'half',
          } as BaseFormFieldConfig,
          {
            name: 'description',
            type: 'textarea',
            label: t('Description'),
            placeholder: t('What is this template used for?'),
            rows: 2,
            gridSize: 'full',
            disabled: isGlobal,
          } as BaseFormFieldConfig,
        ],
      },
      {
        dataTourId: 'table-cols-columns',
        title: t('Columns'),
        description: t('Define the columns (queries) that will be applied to each document.'),
        fields: [
          {
            name: 'columns',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: ColumnsEditor,
            customProps: { isGlobal },
          } as BaseFormFieldConfig,
        ],
      },
    ];

    // Only include sharing section for non-global templates
    if (!isGlobal) {
      sections.push({
        dataTourId: 'table-cols-sharing',
        aside: true,
        title: t('Sharing'),
        description: t('Control who can access this template.'),
        fields: [
          {
            name: 'is_org_wide',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: OrgWideToggle,
            customProps: { title: t('Organization-wide template') },
            disabled: isGlobal,
          } as BaseFormFieldConfig,
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: t('Access'),
            gridSize: 'full',
            disabled: isGlobal,
            hidden: isOrgWide,
          } as BaseFormFieldConfig,
        ],
      });
    }

    return sections;
  }, [t, isGlobal, isOrgWide]);

  // ─── Validation ─────────────────────────────────────────────────────

  const schema = useMemo(() => {
    const fields: any = {};
    fields['name'] = (value: any) => {
      if (!value || !String(value).trim()) {
        return { isValid: false, error: t('Template name is required') };
      }
      return { isValid: true };
    };
    fields['description'] = () => ({ isValid: true });
    fields['category'] = () => ({ isValid: true });
    fields['columns'] = (value: any) => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return { isValid: false, error: t('At least one column is required') };
      }
      for (let i = 0; i < value.length; i++) {
        if (!value[i].name?.trim()) {
          return { isValid: false, error: t('Column {{n}} needs a name', { n: i + 1 }) };
        }
        if (!value[i].query?.trim()) {
          return { isValid: false, error: t('Column {{n}} needs a query', { n: i + 1 }) };
        }
      }
      return { isValid: true };
    };
    fields['is_org_wide'] = () => ({ isValid: true });
    fields['collaborators'] = () => ({ isValid: true });
    return new FormSchema(fields);
  }, [t]);

  // ─── Submit ─────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsSubmitting(true);
      try {
        const orgWide = !!formData.is_org_wide;
        const collaborators: Collaborator[] = formData.collaborators || [];
        const payload = {
          name: String(formData.name).trim(),
          description: formData.description?.trim() || undefined,
          category: formData.category || undefined,
          columns: (formData.columns || []).map((col: ColumnItem, i: number) => ({
            name: col.name.trim(),
            query: col.query.trim(),
            data_type: col.data_type,
            sort_order: i,
          })),
          is_org_wide: orgWide,
          shared_with: orgWide
            ? []
            : collaborators.map((c) => ({
                id: c.user_id,
                collaborator_uuid: c.collaborator_uuid,
                access: c.access || 'view',
              })),
        };

        let result;
        if (isEditing && templateId) {
          result = await dispatch(updateTemplate(templateId, payload) as any);
        } else {
          result = await dispatch(createTemplate(payload) as any);
        }

        if (result) {
          history.push('/templates?type=table-columns');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, isEditing, templateId, history]
  );

  // ─── Cancel ─────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    history.push('/templates?type=table-columns');
  }, [history]);

  // ─── Sync org-wide state for conditional rendering ──────────────────

  const handleValuesChange = useCallback((values: Record<string, any>) => {
    const newOrgWide = !!values.is_org_wide;
    if (newOrgWide !== isOrgWide) {
      setIsOrgWide(newOrgWide);
    }
  }, [isOrgWide]);

  /*
    PONS (built-in) column templates are read-only - but a firm should be
    able to start from one (feedback round 3: "PONS columns should be
    duplicatable & editable"). Duplicate makes a private copy through the
    normal create call and opens it for editing.
  */
  const [isDuplicating, setIsDuplicating] = useState(false);
  const handleDuplicate = useCallback(async () => {
    setIsDuplicating(true);
    try {
      const result: any = await dispatch(createTemplate({
        name: t('{{name}} (copy)', { name: initialValues.name || t('Untitled') }),
        description: initialValues.description || undefined,
        category: initialValues.category || undefined,
        columns: (initialValues.columns || []).map((col: ColumnItem, i: number) => ({
          name: col.name,
          query: col.query,
          data_type: col.data_type,
          sort_order: i,
        })),
        is_org_wide: false,
        shared_with: [],
      } as any) as any);
      const newId = result?.uuid || result?.data?.uuid || result?.id;
      history.push(newId ? `/templates/${newId}?type=table-columns` : '/templates?type=table-columns');
    } finally {
      setIsDuplicating(false);
    }
  }, [dispatch, history, initialValues, t]);

  // ─── Render ─────────────────────────────────────────────────────────

  if (isLoading) {
    return <Skeleton variant="form" rows={6} />;
  }

  // Global PONS templates: fully static read-only view
  if (isGlobal) {
    const cols = initialValues.columns || [];
    return (
      <div className="max-w-3xl mx-auto py-8 px-6 space-y-8">
        {/* Back button */}
        <button
          type="button"
          onClick={() => history.push('/templates?type=table-columns')}
          className="flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-body transition-colors"
        >
          <PiArrowLeftLight className="w-4 h-4 rtl:-scale-x-100" />
          {t('Back to Templates')}
        </button>

        {/*
          The heading, on the glass surface every other header in the app sits
          on. It was bare text at `text-xl font-medium` - a full step heavier
          and larger than the page headings it sits beside, and floating on the
          page background with no surface under it.
        */}
        <div className="glass flex items-start gap-4 rounded-lg p-5 shadow-sm">
          <span
            aria-hidden
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-champagne/30 bg-champagne/10"
          >
            <PiTableLight className="h-5 w-5 text-champagne" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg text-fg-strong">
              {initialValues.name || t('Untitled')}
            </h1>
            {initialValues.description && (
              <p className="mt-1 text-sm text-fg-muted">{initialValues.description}</p>
            )}
            {initialValues.category && (
              <div className="mt-2">
                <Badge color="blue" className="capitalize">
                  {t((initialValues.category || 'general').replace(/_/g, ' '))}
                </Badge>
              </div>
            )}
          </div>
          {/* The way to make it yours - up here, not under a long column list. */}
          <div className="flex-shrink-0">
            <Button
              variant="primary"
              size="small"
              onClick={handleDuplicate}
              isLoading={isDuplicating}
              icon={<PiCopyLight className="h-4 w-4" />}
            >
              {t('Duplicate to edit')}
            </Button>
          </div>
        </div>

        {/* Columns */}
        <GlassCard
          title={t('Columns')}
          subtitle={t('These are the columns (queries) that will be applied to each document.')}
          actions={<Badge color="gray">{cols.length}</Badge>}
        >
          <div className="space-y-3">
            {cols.map((col: ColumnItem, index: number) => (
              <Well key={index} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center pt-0.5">
                    <OrdinalBadge value={index + 1} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-fg-strong">{col.name}</span>
                      <Badge color="gray" className="text-xs">{col.data_type}</Badge>
                    </div>
                    {col.query && (
                      <p className="mt-1 text-sm text-fg-muted">{col.query}</p>
                    )}
                  </div>
                </div>
              </Well>
            ))}
          </div>
        </GlassCard>

        {/* PONS attribution */}
        <div className="flex items-center gap-2 text-sm text-fg-muted pt-4 border-t border-hairline">
          <Logo variant="mark" whiteLabelAware={false} className="h-4 w-4" />
          <span>{t('This is a default template provided by PONS.')}</span>
        </div>
      </div>
    );
  }

  return (
    <>
    <BaseForm
      title={isEditing ? t('Edit Table Columns Template') : t('New Table Columns Template')}
      description={t('Define reusable column templates for batch document analysis.')}
      hideHeader={!isEditing}
      leadIn={
        isEditing ? undefined : (
          <TemplateTypeSwitcher
            current="table-columns"
            variant="header"
            title={t('New Table Columns Template')}
            description={t('Define reusable column templates for batch document analysis.')}
          />
        )
      }
      sections={formSections}
      schema={schema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onValuesChange={handleValuesChange}
      submitLabel={isEditing ? t('Save Changes') : t('Create Template')}
      cancelLabel={t('Cancel')}
      isSubmitting={isSubmitting}
      maxWidth="max-w-6xl"
      hideActions={isGlobal}
    />
    <FeatureTour
      steps={tableColumnsFormTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    <FeatureTour
      steps={tableColumnItemTourSteps}
      run={isColTourRunning}
      onCallback={handleColTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default TableColumnsForm;
